import React, { useState, useMemo, useEffect } from 'react'
import { Outlet, useOutletContext, useLocation } from 'react-router-dom'
import { Content } from './style'
import {
  OptionsButton,
  LeftSide,
  RightSide,
  SectionName,
  SubHeaderContainer,
  ButtonCreate,
  RefreshIcon,
  RefreshIconContainer,
  TabsDiv,
  Tab,
} from '../../../components/styles/subheader-style'
import refresh_act from '../../../assets/refresh-active.svg'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { ViewModeSelector } from '../Pipelines/PipelineControls'
import stock from '../../../assets/stock/TableMovementHeader/list-active.svg'
import resource from '../../../assets/home/resource.svg'
import length from '../../../assets/home/length.svg'

const COMPOSITION_VIEW_MODE_STORAGE_KEY = 'product-development-composition-view-mode'

interface TypeMainOutlet {
  search: string
}

export interface ProductCompositionOutletType {
  refresh: boolean
  search: string
  viewMode: 'board' | 'table'
  isLoading?: boolean
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>
}
const compositionTabs = [
  { path: '/panel/stock', image: stock, name: 'Tecido' },
  { path: '/panel/stock/resource', image: resource, name: 'Aviamentos' },
  { path: '/panel/composition-product', image: length, name: 'Composição de produto' },
]

export const ProductCompositionSubHeader: React.FC<{
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>
  isLoading?: boolean
  viewMode: 'board' | 'table'
  setViewMode: React.Dispatch<React.SetStateAction<'board' | 'table'>>
}> = ({ setRefresh, isLoading, viewMode, setViewMode }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const activeIndex = (() => {
    if (location.pathname.includes('/panel/stock/resource')) return 1
    if (location.pathname.includes('/panel/composition-product')) return 2
    return 0
  })()

  const isFormPage =
    location.pathname.includes('/panel/composition-product/create') ||
    location.pathname.includes('/panel/composition-product/edit')

  return (
    <SubHeaderContainer style={{ zIndex: 20 }}>
      <LeftSide>
        <SectionName>Estoque</SectionName>
        {!isFormPage && (
          <TabsDiv>
            {compositionTabs.map((tab, index) => (
              <Tab
                key={index}
                $isFirst={index === 0}
                $isLast={index === compositionTabs.length - 1}
                $isActive={index === activeIndex}
                onClick={() => {
                  if (index !== activeIndex) {
                    navigate(tab.path)
                  }
                }}
              >
                <img src={tab.image} alt={`${tab.name}-icon`} />
                {tab.name}
              </Tab>
            ))}
          </TabsDiv>
        )}
      </LeftSide>
      <RightSide>
        {!isFormPage && (
          <ViewModeSelector
            value={viewMode}
            onChange={setViewMode}
            boardLabel='Quadro'
            tableLabel='Tabela'
          />
        )}
        <RefreshIconContainer onClick={() => setRefresh(prev => !prev)} title="Atualizar">
          <RefreshIcon src={refresh_act} alt="Atualizar" $spinning={isLoading} />
        </RefreshIconContainer>
        {!isFormPage && viewMode === 'table' && (
          <ButtonCreate onClick={() => navigate('/panel/composition-product/create')}>
            <Plus size={18} />
            Novo produto
          </ButtonCreate>
        )}
      </RightSide>
    </SubHeaderContainer>
  )
}

const ProductCompositionContent = () => {
  const [refresh, setRefresh] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [viewMode, setViewMode] = useState<'board' | 'table'>(() => {
    const persistedMode = localStorage.getItem(COMPOSITION_VIEW_MODE_STORAGE_KEY)
    return persistedMode === 'board' ? 'board' : 'table'
  })
  const mainOutletContext = useOutletContext<TypeMainOutlet | undefined>()
  const search = mainOutletContext?.search || ''
  const location = useLocation()

  const isFormPage =
    location.pathname.includes('/panel/composition-product/create') ||
    location.pathname.includes('/panel/composition-product/edit')

  const isInsideProductDevelopment = location.pathname.startsWith('/panel/composition-product')

  useEffect(() => {
    const handleProductDevelopmentRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string }>).detail
      if (detail?.tab === 'composition') {
        setRefresh(previous => !previous)
      }
    }

    const handleProductDevelopmentViewMode = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string; viewMode?: 'board' | 'table' }>).detail
      if (detail?.tab !== 'composition') {
        return
      }

      if (detail?.viewMode === 'board' || detail?.viewMode === 'table') {
        setViewMode(detail.viewMode)
      }
    }

    window.addEventListener('product-development-refresh', handleProductDevelopmentRefresh as EventListener)
    window.addEventListener('product-development-view-mode', handleProductDevelopmentViewMode as EventListener)

    return () => {
      window.removeEventListener('product-development-refresh', handleProductDevelopmentRefresh as EventListener)
      window.removeEventListener('product-development-view-mode', handleProductDevelopmentViewMode as EventListener)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(COMPOSITION_VIEW_MODE_STORAGE_KEY, viewMode)
  }, [viewMode])

  const outletContext: ProductCompositionOutletType = useMemo(
    () => ({
      refresh,
      search,
      viewMode,
      isLoading,
      setIsLoading,
    }),
    [refresh, search, viewMode, isLoading],
  )

  return (
    <>
      {!isFormPage && !isInsideProductDevelopment && (
        <ProductCompositionSubHeader
          setRefresh={setRefresh}
          isLoading={isLoading}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />
      )}
      <Content>
        <Outlet context={outletContext} />
      </Content>
    </>
  )
}

export default ProductCompositionContent